import { TitleAlign, TitleSize } from '@api/selections';
import { EpiContent, EpiLink } from '@api/types';
import { PageList, PageListItem, SmartLink, Title } from '@components';
import { ContentArea } from '@components/cms';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Container, styled } from '@mui/material';

// TYPES
//-------
export type PageListBlockProps = {
  children?: React.ReactNode;
  // Block title
  title?: string;

  // Block title size - h1, h2, h3
  titleSize?: TitleSize;

  // Title align - left, center, right
  titleAlign?: TitleAlign;

  // Main content area
  content?: EpiContent;

  // Read More link
  link?: EpiLink;

  // Caption for the link
  linkCaption?: string;
  contentItemLinkText?: string;
};

// STYLED COMPONENTS
//-------------------
const PageListBlockWrapper = styled(Container)`
  padding-top: 16px;
  padding-bottom: 16px;
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-top: 20px;
    padding-bottom: 20px;
  }
  &.MuiContainer-root {
    ${({ theme }) => theme.breakpoints.up('md')} {
      padding-right: 80px;
      padding-left: 80px;
    }
  }
`;

const PageListBlockHeader = styled(Box)<{ justifyContent?: string }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
  width: 100%;
  position: relative;
  justify-content: ${(props) => (props.justifyContent ? props.justifyContent : 'center')};
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: 32px;
  }
`;

const PageListBlockLinkWrapper = styled(Box)<{ flex?: number | string }>`
  flex: ${(props) => (props.flex ? props.flex : '1')};
  display: flex;
  justify-content: flex-end;
`;

const PageListBlockButton = styled(Button)`
  width: max-content;
`;

// COMPONENTS
//------------
export const PageListBlock = ({
  title,
  link,
  titleSize,
  content = [],
  titleAlign,
  contentItemLinkText,
}: PageListBlockProps) => {
  const align = titleAlign || 'center';

  return (
    <PageListBlockWrapper className='page-list-block'>
      {(link || title) && (
        <PageListBlockHeader justifyContent={!link ? align : undefined}>
          {link && align !== 'left' && <Box sx={{ flex: 1 }}></Box>}
          {title && (
            <Title variant={titleSize || 'h2'} className='page-list-block-title'>
              {title}
            </Title>
          )}
          {link && (
            <PageListBlockLinkWrapper flex={align !== 'right' ? 1 : undefined}>
              <SmartLink {...link}>
                <PageListBlockButton
                  color='secondary'
                  variant='secondary'
                  endIcon={<ArrowForwardIcon />}
                >
                  {link.title || link.text}
                </PageListBlockButton>
              </SmartLink>
            </PageListBlockLinkWrapper>
          )}
        </PageListBlockHeader>
      )}

      <PageList>
        <ContentArea
          content={content}
          wrapperComponent={(props) => (
            <PageListItem contentItemLinkText={contentItemLinkText} {...props} />
          )}
        />
      </PageList>
    </PageListBlockWrapper>
  );
};
